let serial = 0;

/** Original luminous insect artwork consumes the public number and accepted
 * selected flag only. No future target receives a locator, trail or special glow. */
export function fireflyArtwork(ctx, cell, kit) {
  const art = kit.svg(ctx, 'game-piece-art game-firefly-art');
  if (!Number.isInteger(cell.value) || cell.value < 1 || cell.value > 16) return art;
  art.setAttribute('data-firefly-found', String(Boolean(cell.selected)));
  const id = `firefly-body-${++serial}`;
  const defs = kit.add(ctx, art, 'defs', {});
  const paint = kit.add(ctx, defs, 'radialGradient', { id, cx: '35%', cy: '28%', r: '80%' });
  for (const [offset, colour] of [
    ['0%', 'var(--game-white)'],
    ['35%', 'var(--game-colour-3)'],
    ['100%', 'var(--game-colour-1)'],
  ])
    kit.add(ctx, paint, 'stop', { offset, 'stop-color': colour });
  kit.add(ctx, art, 'ellipse', {
    cx: 50,
    cy: 66,
    rx: 30,
    ry: 30,
    fill: 'var(--game-colour-3)',
    opacity: cell.selected ? 0.2 : 0.06,
  });
  for (const wing of ['M44 53Q3 14 12 51Q18 70 43 65Z', 'M56 53Q97 14 88 51Q82 70 57 65Z'])
    kit.path(ctx, art, wing, {
      fill: 'color-mix(in srgb,var(--game-colour-4) 25%,var(--game-glass))',
      stroke: 'var(--game-colour-0)',
      'stroke-width': 1.4,
      opacity: 0.9,
    });
  kit.path(ctx, art, 'M50 38C25 40 28 82 50 94C72 82 75 40 50 38Z', {
    fill: `url(#${id})`,
    stroke: 'var(--game-colour-3)',
    'stroke-width': 1.2,
  });
  kit.circle(ctx, art, 50, 31, 10, {
    fill: 'var(--game-colour-3)',
    stroke: 'var(--game-white)',
    'stroke-width': 1,
  });
  kit.path(ctx, art, 'M44 24Q35 12 31 16M56 24Q65 12 69 16', {
    fill: 'none',
    stroke: 'var(--game-colour-3)',
    'stroke-width': 2,
    'stroke-linecap': 'round',
  });
  for (const x of [46, 54]) kit.circle(ctx, art, x, 30, 1.6, { fill: 'var(--game-night)' });
  kit.add(
    ctx,
    art,
    'text',
    {
      x: 50,
      y: 74,
      fill: 'var(--game-night)',
      'text-anchor': 'middle',
      'font-family': 'ui-rounded, system-ui, sans-serif',
      'font-weight': 900,
      'font-size': cell.value > 9 ? 28 : 34,
    },
    String(cell.value),
  );
  if (cell.selected)
    kit.path(ctx, art, 'M75 80L81 86L92 73', {
      fill: 'none',
      stroke: 'var(--game-colour-3)',
      'stroke-width': 3,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    });
  return art;
}

/** Draw only the contiguous accepted prefix, in numeric order. Coordinates
 * match the padded zero-gap grid; moves identify a fresh causal effect, not
 * score or policy. Repaint/disconnect/reduced motion cancels old finite effects. */
export function paintFireflyTrail(ctx, host, view, kit, moveSerial = 0) {
  const columns = view.columns;
  const cells = view.cells || [];
  if (!(
    (columns === 3 && cells.length === 9) ||
    (columns === 4 && [12, 16].includes(cells.length))
  )) {
    delete host.fireflySignature;
    delete host.fireflyMove;
    host.replaceChildren();
    return;
  }
  const signature = JSON.stringify([
    columns,
    moveSerial,
    cells.map(({ value, selected }) => [value, !!selected]),
    (view.events || []).map(({ type, cells: indexes }) => [type, indexes]),
  ]);
  if (host.fireflySignature === signature) return;
  const fresh = host.fireflyMove !== moveSerial;
  host.fireflyMove = moveSerial;
  host.fireflySignature = signature;
  const rows = cells.length / columns;
  const art = kit.svg(
    ctx,
    'game-scene-art game-firefly-trail',
    `0 0 ${columns * 100} ${rows * 100}`,
  );
  art.setAttribute('preserveAspectRatio', 'none');
  const locate = (index) => [
    ((index % columns) + 0.5) * 100,
    (Math.floor(index / columns) + 0.5) * 100,
  ];
  const found = new Map(
    cells.flatMap((cell, index) =>
      cell.selected && Number.isInteger(cell.value) ? [[cell.value, index]] : [],
    ),
  );
  const accepted = [];
  for (let value = 1; value <= cells.length && found.has(value); value++)
    accepted.push(found.get(value));
  const reduced =
    typeof window === 'undefined' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const effect = (node, className) => {
    if (!reduced && fresh && host.isConnected && !document.hidden)
      node.setAttribute('class', className);
  };
  for (let index = 0; index < accepted.length; index++) {
    const [x, y] = locate(accepted[index]);
    kit.circle(ctx, art, x, y, 21, { fill: 'var(--game-colour-3)', opacity: 0.14 });
    if (!index) continue;
    const [fromX, fromY] = locate(accepted[index - 1]);
    const line = kit.path(ctx, art, `M${fromX} ${fromY}L${x} ${y}`, {
      fill: 'none',
      stroke: 'var(--game-colour-3)',
      'stroke-width': 4,
      'stroke-linecap': 'round',
      opacity: 0.7,
      pathLength: 1,
      'data-firefly-from': index,
      'data-firefly-to': index + 1,
    });
    if (
      index === accepted.length - 1 &&
      view.events?.some((event) => event.type === 'find' && event.cells?.includes(accepted[index]))
    )
      effect(line, 'firefly-edge-new');
  }
  const miss = view.events?.find((event) => event.type === 'miss');
  const index = miss?.cells?.[0];
  if (Number.isInteger(index) && index >= 0 && index < cells.length && !cells[index].selected) {
    const [x, y] = locate(index);
    const ring = kit.circle(ctx, art, x, y, 39, {
      fill: 'none',
      stroke: 'var(--game-colour-1)',
      'stroke-width': 3,
      opacity: 0.75,
      'data-firefly-miss': 'true',
    });
    effect(ring, 'firefly-miss-new');
  }
  host.replaceChildren(art);
}
